import { useEffect } from 'react';
import { useTranslate } from 'core/hooks/useTranslate';

function useInputText({ icon, iconAriaLabel, onClickIcon, hasCustomOnClickIcon, referer, ref }) {
  const { t } = useTranslate();

  const refInput = referer ? referer : ref;

  const getIconAriaLabel = (iconType) => {
    const iconLabels = {
      visible: t('buttons:Show password'),
      'visible-off': t('buttons:Hide password'),
      search: t('buttons:Search'),
      clear: t('buttons:Clear field'),
      calendar: t('buttons:Open calendar'),
      eye: t('buttons:Show password'),
      'eye-off': t('buttons:Hide password'),
    };

    return iconLabels[iconType] || t('buttons:Click {{icon}} icon', { icon: iconType });
  };

  useEffect(() => {
    let isMouseDown = false;
    let keyboardNavTimeout = null;

    function handleKeyDown(e) {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-nav');
        // Clear previous timeout if it exists
        if (keyboardNavTimeout) {
          clearTimeout(keyboardNavTimeout);
        }
        // Reset after a delay to allow focusin to process
        keyboardNavTimeout = setTimeout(() => {
          document.body.classList.remove('keyboard-nav');
        }, 150);
      }
    }

    function handleMouseDown() {
      isMouseDown = true;
      document.body.classList.remove('keyboard-nav');
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
        keyboardNavTimeout = null;
      }
      // Reset after a brief delay
      setTimeout(() => {
        isMouseDown = false;
      }, 0);
    }

    function handleFocusIn(e) {
      if (e.target.classList.contains('input-text__input')) {
        if (document.body.classList.contains('keyboard-nav') && !isMouseDown) {
          e.target.classList.add('focused-by-keyboard');
          e.target.classList.remove('focused-by-mouse');
        } else {
          e.target.classList.add('focused-by-mouse');
          e.target.classList.remove('focused-by-keyboard');
        }
      }
    }

    function handleFocusOut(e) {
      if (e.target.classList.contains('input-text__input')) {
        e.target.classList.remove('focused-by-keyboard', 'focused-by-mouse');
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
      }
      document.body.classList.remove('keyboard-nav');
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  const isIconClickable = hasCustomOnClickIcon;

  const getIconAriaLabelValue = icon ? (iconAriaLabel || getIconAriaLabel(icon)) : undefined;

  const handleIconKeyDown = isIconClickable
    ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickIcon();
        }
      }
    : undefined;

  return {
    refInput,
    isIconClickable,
    getIconAriaLabelValue,
    handleIconKeyDown,
  };
}

export default useInputText;
